import { useCallback, useMemo, useState } from 'react';
import type { Mode } from '@timato/shared';
import { ConfirmDialog } from '@/components/ui';
import { useProjects } from '@/data/hooks';
import { primeAudio } from '@/lib/chime';
import { useCompletionStore } from '@/timer/completionStore';
import { MODE_LABEL } from '@/timer/engine';
import { startModeInstead } from '@/timer/startTimer';
import { describeLive } from './describeRun';
import { useProjectMap } from '@/data/hooks';
import { projectColor } from '@/lib/projectColors';
import { useIsMobile } from '@/lib/useMediaQuery';
import { useShortcuts } from '@/lib/useShortcuts';
import { useTimer } from '@/timer/useTimer';
import { EndOfSessionCard } from './EndOfSessionCard';
import { FullscreenTimer } from './FullscreenTimer';
import { GoalPanel } from './GoalPanel';
import { GoalRow } from './GoalRow';
import { ModeSwitch } from './ModeSwitch';
import { RunningElsewhere } from './RunningElsewhere';
import { SwitchTo } from './SwitchTo';
import { TimerControls } from './TimerControls';
import { TimerDisplay } from './TimerDisplay';
import { TodayByProject } from './TodayByProject';
import { useSwitcher } from './useSwitchTargets';
import { switchKey } from '@/timer/switchTargets';

/**
 * Three columns, vertically centred, with the ring in the middle (4a/8a): goal on the left, "Today by project" on the
 * right. The chrome recedes; nothing here sits in a card except the mode switch.
 */
export function TimerPage() {
  // TW-33: a tab picked while a run is live previews that mode; Start then asks before the run is parked or stopped.
  const [view, setView] = useState<{ mode: Mode; runId: string | null } | null>(null);
  const [asking, setAsking] = useState(false);
  const ask = useCallback(() => setAsking(true), []);
  const api = useTimer(useMemo(() => (view ? { ...view, onStart: ask } : null), [view, ask]));
  const allProjects = useProjects();
  const projects = useProjectMap();
  const project = api.timer.projectId ? projects.get(api.timer.projectId) : undefined;
  const color = projectColor(project);
  const { actions, timer, live, previewing, isCountdown } = api;
  // A tab with a run going only previews: the run is never reset by a click, which used to throw its time away.
  const { status: liveStatus, mode: liveMode, runId: liveRunId } = live;
  const pickMode = useCallback((m: Mode) => {
    if (liveStatus === 'idle') { setView(null); void actions.setMode(m); return; }
    setView(m === liveMode ? null : { mode: m, runId: liveRunId });
  }, [liveStatus, liveMode, liveRunId, actions]);
  const startInstead = async () => {
    if (!view) return;
    primeAudio();
    useCompletionStore.getState().set(null);
    await startModeInstead(view.mode);
    setView(null);
  };
  const [fullscreen, setFullscreen] = useState(false);
  const exitFullscreen = useCallback(() => setFullscreen(false), []);
  const mobile = useIsMobile();
  const switcher = useSwitcher(live, api.now); // the run that is going, not a previewed mode
  const { targets, switchTo } = switcher;

  useShortcuts(useMemo(() => [
    { key: ' ', handler: () => void actions.toggle() },
    // TW-30: a number key jumps to the row printed with it under the goal.
    ...targets.map((t, i) => ({ key: switchKey(i), handler: () => void switchTo(t) })),
    { key: 'r', handler: () => { if (timer.status !== 'idle') void actions.reset(); } },
    { key: 's', handler: () => void (isCountdown ? actions.skip() : timer.status !== 'idle' && actions.finish()) },
    { key: 't', handler: () => pickMode(timer.mode === 'track' ? 'focus' : 'track') },
    { key: 'f', handler: () => setFullscreen((f) => !f) },
  ], [actions, timer.mode, timer.status, isCountdown, targets, switchTo, pickMode]));

  return (
    <div className="flex-1 grid items-center px-4 pb-6 md:px-[60px] md:pb-6 lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-8 max-w-[1400px] mx-auto">
      {mobile ? <div className="flex flex-col gap-3 min-w-0"><GoalRow api={api} /><SwitchTo switcher={switcher} compact /></div> : <GoalPanel api={api}><SwitchTo switcher={switcher} /></GoalPanel>}
      <section className="flex flex-col items-center gap-5 md:gap-[22px] py-4" aria-label="Timer">
        <ModeSwitch value={timer.mode} onChange={pickMode} live={previewing ? live.mode : null} />
        <TimerDisplay api={api} color={color} />
        {/* The line under the controls comes and goes; hung below them rather than in the column, it moves nothing.
            In the column, the centred ring jumped by its height on every tab picked over a running timer. */}
        <div className="relative">
          <TimerControls api={api} project={project} />
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[min(420px,calc(100vw-32px))] text-center">
            <RunningElsewhere api={api} />
            {previewing && (
              <p className="text-meta-sm text-ink-2 m-0">
                {describeLive(live, allProjects)} ·{' '}
                <button type="button" onClick={() => setView(null)} className="p-0 border-0 bg-transparent font-bold text-accent-text cursor-pointer hover:underline">Back to it</button>
              </p>
            )}
          </div>
        </div>
      </section>
      <TodayByProject now={api.now} />
      <EndOfSessionCard api={api} />
      {fullscreen && <FullscreenTimer api={api} onExit={exitFullscreen} />}
      <ConfirmDialog open={asking && previewing} onOpenChange={setAsking} title={`Start ${MODE_LABEL[timer.mode]}?`}
        confirmLabel={live.mode === 'track' ? 'Park it and start' : 'Stop it and start'} onConfirm={startInstead}>
        {describeLive(live, allProjects)}.{' '}
        {live.mode === 'track' ? 'It is parked, to pick up again from Parked timers.' : 'It stops here, and the time so far is logged.'}
      </ConfirmDialog>
    </div>
  );
}
